import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  UserCard,
  toUserCardProps,
  renderFallback,
  createColumnHeader,
  createDueDateColumn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { FilePen, HelpCircle } from 'lucide-react';
import { useStandardRecordingTimeMs } from '@/hooks/useStandardRecordingTime';
import { ProcessStepCell } from '../components/ProcessStepCell';
import { isUnresolvedStepId } from '../utils/processStepInfo';
import type { AssignmentRow } from '../types';
import type { ColumnHelpersParams } from './useMyTasksColumns';

export function useAssignmentsColumns(params: ColumnHelpersParams) {
  const { t } = useTranslation();
  const {
    slug,
    getProcessName,
    getDepartment,
    getProcessStepInfo,
    setSelectedAssignment,
    setSelectedReassignment
  } = params;
  const standardRecordingTimeMs = useStandardRecordingTimeMs();

  return useMemo<DataTableColumnDef<AssignmentRow>[]>(
    () => [
      {
        accessorKey: 'sourceProcessStep',
        meta: { label: t('myTasks.processStep'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.processStep')),
        cell: ({ row }) => {
          // Unclear resolution task - needs different visual to distinguish from regular step assignments
          if (row.original.status === 'pending-unclear') {
            return (
              <div className="max-w-sm overflow-hidden">
                <Item variant="default" size="sm" className="w-full">
                  <ItemMedia variant="icon" className="shrink-0">
                    <HelpCircle className="size-4" />
                  </ItemMedia>
                  <ItemContent className="min-w-0 overflow-hidden">
                    <ItemTitle className="truncate">{t('myTasks.unclearResolution')}</ItemTitle>
                  </ItemContent>
                </Item>
              </div>
            );
          }
          return (
            <div className="max-w-sm overflow-hidden">
              <Item variant="default" size="sm" className="w-full">
                <ItemMedia variant="icon" className="shrink-0">
                  <FilePen className="size-4" />
                </ItemMedia>
                <ItemContent className="min-w-0 overflow-hidden">
                  <ItemTitle className="truncate">{t('myTasks.newStep')}</ItemTitle>
                </ItemContent>
              </Item>
            </div>
          );
        },
        accessorFn: (row) => {
          if (row.status === 'pending-unclear') {
            return t('myTasks.unclearResolution');
          }
          return t('myTasks.newStep');
        },
        enableColumnFilter: true
      },
      {
        id: 'previousStep',
        accessorKey: 'previousStepSearchText',
        meta: { label: t('myTasks.previousProcessStep'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.previousProcessStep')),
        cell: ({ row }) => {
          if (isUnresolvedStepId(row.original.sourceProcessStep)) return renderFallback(null);
          return (
            <ProcessStepCell
              step={row.original.sourceProcessStep}
              ruleIds={row.original.ruleIds}
              assignment={row.original}
              getStepInfo={getProcessStepInfo}
            />
          );
        },
        enableColumnFilter: true
      },
      {
        accessorKey: 'process',
        id: 'processName',
        meta: { label: t('myTasks.process'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.process')),
        cell: ({ row }) => (
          <Link
            to="/$slug/processes/all-processes/$processId"
            params={{
              slug,
              processId:
                typeof row.original.process === 'string'
                  ? row.original.process
                  : row.original.process._id
            }}
            className="font-medium hover:underline"
          >
            {getProcessName(row.original.process)}
          </Link>
        ),
        accessorFn: (row) => getProcessName(row.process),
        enableColumnFilter: true
      },
      {
        id: 'department',
        meta: { label: t('myTasks.department'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.department')),
        cell: ({ row }) => getDepartment(row.original.process),
        accessorFn: (row) => getDepartment(row.process),
        enableColumnFilter: true
      },
      {
        id: 'assignedBy',
        meta: { label: t('myTasks.assignedBy'), filterType: 'text' },
        header: createColumnHeader(t('myTasks.assignedBy')),
        cell: ({ row }) => {
          const user = row.original.assignedBy;

          if (!user || typeof user === 'string') return renderFallback(null);

          return (
            <UserCard
              user={toUserCardProps(user)}
              variant="with-role"
              itemVariant="outline"
              size="sm"
            />
          );
        },
        accessorFn: (row) => {
          const user = row.assignedBy;
          if (!user || typeof user === 'string') return '';
          return `${user.firstName} ${user.lastName}`;
        },
        enableColumnFilter: true
      },
      createDueDateColumn<AssignmentRow>(t('myTasks.dueDate'), standardRecordingTimeMs),
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => {
          const assignment = row.original;
          if (assignment.status === 'pending') {
            return (
              <div className="flex justify-end">
                <Button
                  size="small"
                  variant="primary"
                  onClick={() => setSelectedAssignment(assignment)}
                >
                  {t('processPage.Check')}
                </Button>
              </div>
            );
          }
          if (assignment.status === 'accepted' && assignment.processStepRecording) {
            return (
              <div className="flex justify-end">
                <Button size="small" variant="primary" asChild>
                  <Link
                    to="/$slug/processes/recording/step-recording/$recordingId"
                    params={{ slug, recordingId: assignment.processStepRecording }}
                  >
                    {t('common.Continue')}
                  </Link>
                </Button>
              </div>
            );
          }
          if (assignment.status === 'pending-unclear') {
            return (
              <div className="flex justify-end">
                <Button
                  size="small"
                  variant="primary"
                  onClick={() => setSelectedReassignment(assignment)}
                >
                  {t('processPage.Reassign')}
                </Button>
              </div>
            );
          }
          return null;
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [
      t,
      slug,
      getProcessName,
      getDepartment,
      getProcessStepInfo,
      setSelectedAssignment,
      setSelectedReassignment,
      standardRecordingTimeMs
    ]
  );
}
